import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams, useParams, Link } from "react-router-dom";
import Footer from "../components/Layout/Footer";
import Header from "../components/Layout/Header";
import Loader from "../components/Layout/Loader";
import ProductCard from "../components/Route/ProductCard/ProductCard";
import styles from "../styles/styles";
import { categoriesData } from "../static/data";
import { fetchSiteOptions } from "../redux/actions/siteOptions";
import { backend_url } from "../server";
import { AiOutlineDown, AiOutlineUp, AiOutlineSearch } from "react-icons/ai";

    const toSlug = (value) =>
      (value || "")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/&/g, " ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const resolveImageUrl = (value) => {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:")) return value;
  const path = value.startsWith("/") ? value : `/${value}`;
  return `${backend_url}${path}`;
};

/** Must be defined outside ProductsPage so its identity is stable — inner components remount every render and drop input focus. */
const FilterSection = ({ id, title, children, openSection, setOpenSection }) => {
  const isOpen = openSection === id;
  return (
    <div className="border-b border-slate-200">
      <button
        type="button"
        onClick={() => setOpenSection(isOpen ? "" : id)}
        className="flex w-full items-center justify-between py-3 text-left font-medium text-slate-800"
      >
        {title}
        {isOpen ? <AiOutlineUp className="h-4 w-4" /> : <AiOutlineDown className="h-4 w-4 rotate-[-90deg]" />}
      </button>
      {isOpen && <div className="pb-3">{children}</div>}
    </div>
  );
};

const ProductsPage = () => {
  const { mainSlug, subSlug } = useParams();
  const [searchParams] = useSearchParams();
  const categoryQuery = searchParams.get("category");
  const dispatch = useDispatch();
  const { allProducts = [], isLoading } = useSelector((state) => state.products);
  const siteOptionsState = useSelector((state) => state.siteOptions);

  const catalogCategories = siteOptionsState?.options?.global?.catalog?.categories || [];
  const catalogLength = catalogCategories.length;
  const productsLength = (allProducts || []).length;

  useEffect(() => {
    if (!siteOptionsState?.options?.global) {
      dispatch(fetchSiteOptions("global"));
    }
  }, [dispatch, siteOptionsState?.options?.global]);

  const { viewMode, data, bannerTitle, mainCategory, subcategories } = useMemo(() => {
    const categories = siteOptionsState?.options?.global?.catalog?.categories || [];
    const mainSlugNorm = mainSlug ? toSlug(mainSlug) : null;
    const subSlugNorm = subSlug ? toSlug(subSlug) : null;
    const decodedQuery = categoryQuery ? decodeURIComponent(categoryQuery).trim() : "";
    const querySlug = decodedQuery ? toSlug(decodedQuery) : null;

    if (!mainSlugNorm && !querySlug) {
      return { viewMode: "categories", data: [], bannerTitle: "Products", mainCategory: null, subcategories: [] };
    }

    const slugToUse = mainSlugNorm || querySlug;
    if (slugToUse === "all") {
      return {
        viewMode: "products",
        data: allProducts || [],
        bannerTitle: "All Products",
        mainCategory: null,
        subcategories: [],
      };
    }
    const mainCat = categories.find(
      (c) => c.isActive !== false && (toSlug(c.slug) === slugToUse || toSlug(c.name) === slugToUse)
    ) || categoriesData.find((c) => {
      const linkMatch = c.link?.match(/category=([^&]+)/);
      if (linkMatch) return toSlug(decodeURIComponent(linkMatch[1])) === slugToUse;
      return false;
    });

    if (!mainCat) {
      if (slugToUse === "discount-zone") {
        const bestDeals = (allProducts || []).filter(
          (p) => p.originalPrice != null && p.originalPrice > 0 && p.discountPrice != null && Number(p.discountPrice) < Number(p.originalPrice)
        );
        return { viewMode: "products", data: bestDeals, bannerTitle: "Discount Zone", mainCategory: null, subcategories: [] };
      }
      if (mainSlugNorm && subSlugNorm) {
        const filtered = (allProducts || []).filter((p) => {
          const pc = (p.category || "").trim();
          if (!pc.includes(" > ")) return false;
          const [m, s] = pc.split(" > ").map((x) => x.trim());
          return toSlug(m) === mainSlugNorm && toSlug(s) === subSlugNorm;
        });
        return { viewMode: "products", data: filtered, bannerTitle: subSlugNorm.replace(/-/g, " "), mainCategory: null, subcategories: [] };
      }
      const filtered = (allProducts || []).filter((p) => toSlug(p.category) === slugToUse || (p.category || "").includes(" > ") && toSlug(p.category.split(" > ")[0]) === slugToUse);
      return { viewMode: "products", data: filtered, bannerTitle: decodedQuery || mainSlug || "Products", mainCategory: null, subcategories: [] };
    }

    const mainName = mainCat.name || mainCat.title || "";
    const subs = Array.isArray(mainCat.subcategories) ? mainCat.subcategories.filter((s) => s.isActive !== false) : [];

    if (subSlugNorm === "all" && subs.length > 0) {
      const filtered = (allProducts || []).filter((p) => {
        const pc = (p.category || "").trim();
        if (!pc) return false;
        if (toSlug(pc) === toSlug(mainName)) return true;
        if (!pc.includes(" > ")) return false;
        const [m] = pc.split(" > ").map((x) => x.trim());
        return toSlug(m) === toSlug(mainName);
      });
      return {
        viewMode: "products",
        data: filtered,
        bannerTitle: `${mainName} — All`,
        mainCategory: mainCat,
        subcategories: subs,
      };
    }

    if (subSlugNorm && subs.length > 0) {
      const subCat = subs.find((s) => toSlug(s.slug) === subSlugNorm || toSlug(s.name) === subSlugNorm);
      const filtered = (allProducts || []).filter((p) => {
        const pc = (p.category || "").trim();
        if (!pc.includes(" > ")) return false;
        const [m, s] = pc.split(" > ").map((x) => x.trim());
        return toSlug(m) === toSlug(mainName) && toSlug(s) === subSlugNorm;
      });
      return { viewMode: "products", data: filtered, bannerTitle: subCat ? (subCat.name || "") : subSlugNorm.replace(/-/g, " "), mainCategory: mainCat, subcategories: subs };
    }

    if (subs.length > 0 && !subSlugNorm) {
      return { viewMode: "subcategories", data: [], bannerTitle: mainName, mainCategory: mainCat, subcategories: subs };
    }

    const filtered = (allProducts || []).filter((p) => {
      const pc = p.category || "";
      if (toSlug(pc) === slugToUse) return true;
      if (pc.includes(" > ")) return toSlug(pc.split(" > ")[0].trim()) === slugToUse;
      return false;
    });
    return { viewMode: "products", data: filtered, bannerTitle: mainName, mainCategory: mainCat, subcategories: [] };
  }, [mainSlug, subSlug, categoryQuery, catalogLength, productsLength, allProducts]);

  const sortedCategories = [...catalogCategories]
    .filter((c) => c.isActive !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const sortedSubcategories = [...subcategories].sort((a, b) => (a.order || 0) - (b.order || 0));

  // --- Filters (only when viewMode === "products") ---
  const [searchQuery, setSearchQuery] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [selectedBrands, setSelectedBrands] = useState(() => new Set());
  const [selectedWeaponPlatforms, setSelectedWeaponPlatforms] = useState(() => new Set());
  const [selectedExternalPartTypes, setSelectedExternalPartTypes] = useState(() => new Set());
  const [selectedColors, setSelectedColors] = useState(() => new Set());
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [sortBy, setSortBy] = useState("default");
  const [openFilterSection, setOpenFilterSection] = useState("platform");
  const [platformSearch, setPlatformSearch] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const INITIAL_VISIBLE = 20;
  const LOAD_MORE_STEP = 20;
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const productFilters = siteOptionsState?.options?.global?.productFilters || {};
  const filterGroups = useMemo(() => {
    const groups = Array.isArray(productFilters.groups) ? productFilters.groups : [];
    if (groups.length > 0) {
      return groups
        .filter((group) => group && group.isActive !== false && group.key && group.label)
        .map((group, index) => ({
          key: String(group.key || "").trim(),
          label: String(group.label || "").trim(),
          options: Array.isArray(group.options)
            ? group.options.filter((item) => item && item.name).sort((a, b) => (a.order || 0) - (b.order || 0))
            : [],
          visibleCategories: Array.isArray(group.visibleCategories) ? group.visibleCategories : [],
          order: typeof group.order === "number" ? group.order : index,
        }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return [
      { key: "weaponPlatforms", label: "Weapon platform", options: productFilters.weaponPlatforms || [], visibleCategories: productFilters?.visibility?.weaponPlatforms || [], order: 0 },
      { key: "externalPartTypes", label: "External part type", options: productFilters.externalPartTypes || [], visibleCategories: productFilters?.visibility?.externalPartTypes || [], order: 1 },
      { key: "brands", label: "Brand", options: productFilters.brands || [], visibleCategories: productFilters?.visibility?.brands || [], order: 2 },
      { key: "colors", label: "Primary colour", options: productFilters.colors || [], visibleCategories: productFilters?.visibility?.colors || [], order: 3 },
    ].map((group) => ({
      ...group,
      options: (group.options || []).filter((item) => item && item.name).sort((a, b) => (a.order || 0) - (b.order || 0)),
    }));
  }, [productFilters]);

  // Determine current catalog category slug for filter visibility
  const currentCategorySlug = useMemo(() => {
    if (!mainCategory) return null;
    const slug = mainCategory.slug || mainCategory.name || mainCategory.title || "";
    return slug || null;
  }, [mainCategory]);

  const isFilterVisibleForCategory = useCallback(
    (group) => {
      if (!currentCategorySlug) return true;
      const list = group.visibleCategories;
      if (!Array.isArray(list) || list.length === 0) return true;
      return list.includes(currentCategorySlug);
    },
    [currentCategorySlug]
  );

  const countsInData = useMemo(() => {
    if (viewMode !== "products" || !Array.isArray(data)) return {};
    const counts = {};
    data.forEach((p) => {
      filterGroups.forEach((group) => {
        if (!counts[group.key]) counts[group.key] = {};
        const dynamicValue = (p?.filterValues && p.filterValues[group.key]) || "";
        const legacyValue =
          group.key === "brands"
            ? p.brand
            : group.key === "colors"
            ? p.color
            : group.key === "weaponPlatforms"
            ? p.weaponPlatform
            : group.key === "externalPartTypes"
            ? p.externalPartType
            : "";
        const value = String(dynamicValue || legacyValue || "").trim();
        if (value) counts[group.key][value] = (counts[group.key][value] || 0) + 1;
      });
    });
    return counts;
  }, [viewMode, data, filterGroups]);

  const clearAllFilters = useCallback(() => {
    setSearchQuery("");
    setInStockOnly(false);
    setPriceMin("");
    setPriceMax("");
    setSelectedBrands(new Set());
    setSelectedWeaponPlatforms(new Set());
    setSelectedExternalPartTypes(new Set());
    setSelectedColors(new Set());
    setSelectedFilterValues({});
    setPlatformSearch("");
    setBrandSearch("");
  }, []);

  const filteredAndSortedProducts = useMemo(() => {
    if (viewMode !== "products" || !Array.isArray(data)) return data;
    let list = [...data];
    const q = (searchQuery || "").toLowerCase().trim();
    if (q) {
      list = list.filter((p) => {
        const name = (p.name || "").toLowerCase();
        const desc = (p.description || "").replace(/<[^>]+>/g, " ").toLowerCase();
        const cat = (p.category || "").toLowerCase();
        const tags = (p.tags || "").toLowerCase();
        return name.includes(q) || desc.includes(q) || cat.includes(q) || tags.includes(q);
      });
    }
    if (inStockOnly) list = list.filter((p) => Number(p.stock) > 0);
    const min = priceMin !== "" && priceMin !== null ? Number(priceMin) : null;
    const max = priceMax !== "" && priceMax !== null ? Number(priceMax) : null;
    if (min != null && !Number.isNaN(min)) list = list.filter((p) => Number(p.discountPrice ?? p.originalPrice ?? 0) >= min);
    if (max != null && !Number.isNaN(max)) list = list.filter((p) => Number(p.discountPrice ?? p.originalPrice ?? 0) <= max);
    if (selectedBrands.size > 0) list = list.filter((p) => (p.brand || "").trim() && selectedBrands.has((p.brand || "").trim()));
    if (selectedColors.size > 0) list = list.filter((p) => (p.color || "").trim() && selectedColors.has((p.color || "").trim()));
    if (selectedWeaponPlatforms.size > 0) list = list.filter((p) => (p.weaponPlatform || "").trim() && selectedWeaponPlatforms.has((p.weaponPlatform || "").trim()));
    if (selectedExternalPartTypes.size > 0) list = list.filter((p) => (p.externalPartType || "").trim() && selectedExternalPartTypes.has((p.externalPartType || "").trim()));
    const activeDynamicFilters = Object.entries(selectedFilterValues).filter(
      ([, selected]) => selected instanceof Set && selected.size > 0
    );
    if (activeDynamicFilters.length > 0) {
      list = list.filter((product) =>
        activeDynamicFilters.every(([groupKey, selected]) => {
          const dynamicValue = product?.filterValues?.[groupKey];
          const legacyValue =
            groupKey === "brands"
              ? product.brand
              : groupKey === "colors"
              ? product.color
              : groupKey === "weaponPlatforms"
              ? product.weaponPlatform
              : groupKey === "externalPartTypes"
              ? product.externalPartType
              : "";
          const value = String(dynamicValue || legacyValue || "").trim();
          return value && selected.has(value);
        })
      );
    }
    if (sortBy === "price-asc") list.sort((a, b) => (Number(a.discountPrice ?? a.originalPrice ?? 0) - Number(b.discountPrice ?? b.originalPrice ?? 0)));
    else if (sortBy === "price-desc") list.sort((a, b) => (Number(b.discountPrice ?? b.originalPrice ?? 0) - Number(a.discountPrice ?? a.originalPrice ?? 0)));
    else if (sortBy === "name-asc") list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortBy === "name-desc") list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    return list;
  }, [viewMode, data, searchQuery, inStockOnly, priceMin, priceMax, selectedBrands, selectedWeaponPlatforms, selectedExternalPartTypes, selectedColors, selectedFilterValues, sortBy]);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [
    viewMode,
    data?.length,
    searchQuery,
    inStockOnly,
    priceMin,
    priceMax,
    selectedBrands,
    selectedWeaponPlatforms,
    selectedExternalPartTypes,
    selectedColors,
    selectedFilterValues,
    sortBy,
  ]);

  const hasActiveFilters =
    searchQuery ||
    inStockOnly ||
    priceMin !== "" ||
    priceMax !== "" ||
    selectedBrands.size > 0 ||
    selectedWeaponPlatforms.size > 0 ||
    selectedExternalPartTypes.size > 0 ||
    selectedColors.size > 0 ||
    Object.values(selectedFilterValues).some((set) => set instanceof Set && set.size > 0);

  return (
    <>
      {isLoading && viewMode === "products" && data.length === 0 ? (
        <Loader />
      ) : (
        <div>
          <Header activeHeading={2} />
          <div className="bg-[#CCBEA1] border-t border-t-black" id="inner-page-banner">
            <div className="py-[50px] flex flex-col px-4 text-center">
              <h1 className="lg:text-4xl text-3xl md:mb-0 mb-6 lg:leading-normal font-semibold">
                {bannerTitle}
              </h1>
              <p>
                <Link to="/" className="hover:text-[#38513B]">Home</Link>
                {" "}&gt;{" "}
                {!mainCategory ? (
                  <span className="text-[#38513B]">Products</span>
                ) : (
                  <>
                    <Link to="/products" className="hover:text-[#38513B]">Products</Link>
                    {" "}&gt;{" "}
                    {viewMode === "subcategories" ? (
                      <span className="text-[#38513B]">{mainCategory.name || mainCategory.title}</span>
                    ) : viewMode === "products" && subSlug && mainCategory ? (
                      <>
                        <Link to={`/products/${toSlug(mainCategory.slug || mainCategory.name)}`} className="hover:text-[#38513B]">{mainCategory.name || mainCategory.title}</Link>
                        {" "}&gt;{" "}
                        <span className="text-[#38513B]">{bannerTitle}</span>
                      </>
                    ) : (
                      <span className="text-[#38513B]">{mainCategory.name || mainCategory.title}</span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className={`${styles.section} py-8`}>
            {viewMode === "categories" && (
              <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                <Link
                  to="/products/all"
                  className="group block rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:border-[#38513b] hover:shadow-md transition overflow-hidden"
                >
                  <div className="aspect-square w-full rounded-lg mb-3 flex items-center justify-center bg-gradient-to-br from-[#f3f7f3] to-[#e8efe9] border border-[#38513b]/15">
                    <span className="text-4xl font-semibold tracking-tight text-[#38513b]">All</span>
                  </div>
                  <h2 className="text-base font-semibold text-slate-900 group-hover:text-[#38513b] leading-tight">
                    View all products
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">Browse all categories</p>
                </Link>
                {sortedCategories.map((cat) => {
                  const slug = toSlug(cat.slug || cat.name);
                  const href = `/products/${slug}`;
                  const imgSrc = resolveImageUrl(cat.image || cat.icon || "");
                  return (
                    <Link
                      key={cat._id || slug}
                      to={href}
                      className="group block rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:border-[#38513b] hover:shadow-md transition overflow-hidden"
                    >
                      <div className="aspect-square w-full rounded-lg overflow-hidden mb-3">
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={cat.name}
                            className="h-full w-full object-contain group-hover:scale-105 transition"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-slate-400 text-4xl font-light">{cat.name ? cat.name.charAt(0) : "?"}</div>
                        )}
                      </div>
                      <h2 className="text-base font-semibold text-slate-900 group-hover:text-[#38513b]">{cat.name || cat.title}</h2>
                    </Link>
                  );
                })}
              </div>
            )}

            {viewMode === "subcategories" && (
              <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
                <Link
                  to={`/products/${toSlug(mainCategory?.slug || mainCategory?.name)}/all`}
                  className="group block rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:border-[#38513b] hover:shadow-md transition overflow-hidden"
                >
                  <div className="aspect-square w-full rounded-lg mb-3 flex items-center justify-center bg-gradient-to-br from-[#f3f7f3] to-[#e8efe9] border border-[#38513b]/15">
                    <span className="text-4xl font-semibold tracking-tight text-[#38513b]">All</span>
                  </div>
                  <h2 className="text-base font-semibold text-slate-900 group-hover:text-[#38513b] leading-tight">
                    View all in {mainCategory?.name || mainCategory?.title || "category"}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">Include every subcategory</p>
                </Link>
                {sortedSubcategories.map((sub) => {
                  const subSlugVal = toSlug(sub.slug || sub.name);
                  const href = `/products/${toSlug(mainCategory.slug || mainCategory.name)}/${subSlugVal}`;
                  const subImgSrc = resolveImageUrl(sub.image || sub.icon || "");
                  return (
                    <Link
                      key={sub._id || subSlugVal}
                      to={href}
                      className="group block rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:border-[#38513b] hover:shadow-md transition overflow-hidden"
                    >
                      <div className="aspect-square w-full rounded-lg overflow-hidden mb-3">
                        {subImgSrc ? (
                          <img
                            src={subImgSrc}
                            alt={sub.name}
                            className="h-full w-full object-contain group-hover:scale-105 transition"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-slate-400 text-4xl font-light">{sub.name ? sub.name.charAt(0) : "?"}</div>
                        )}
                      </div>
                      <h2 className="text-base font-semibold text-slate-900 group-hover:text-[#38513b]">{sub.name}</h2>
                      <p className="text-sm text-slate-500 mt-1">View products</p>
                    </Link>
                  );
                })}
              </div>
            )}

            {viewMode === "products" && (
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Left sidebar - Refine by */}
                <aside className="w-full lg:w-80 shrink-0">
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sticky top-4">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="font-semibold text-slate-800">Refine by</h2>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearAllFilters}
                          className="text-sm text-[#38513b] hover:underline"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="space-y-0">
                      <FilterSection
                        id="search"
                        title="Search"
                        openSection={openFilterSection}
                        setOpenSection={setOpenFilterSection}
                      >
                        <div className="relative">
                          <AiOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search products..."
                            className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                          />
                        </div>
                      </FilterSection>
                      <FilterSection
                        id="stock"
                        title="In stock"
                        openSection={openFilterSection}
                        setOpenSection={setOpenFilterSection}
                      >
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={inStockOnly}
                            onChange={(e) => setInStockOnly(e.target.checked)}
                            className="rounded border-slate-300 text-[#38513b] focus:ring-[#38513b]"
                          />
                          <span className="text-sm text-slate-700">In stock only</span>
                        </label>
                      </FilterSection>
                      <FilterSection
                        id="price"
                        title={`Price (£${priceMin === "" ? "0" : priceMin} - £${priceMax === "" ? "∞" : priceMax})`}
                        openSection={openFilterSection}
                        setOpenSection={setOpenFilterSection}
                      >
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={priceMin}
                            onChange={(e) => setPriceMin(e.target.value)}
                            placeholder="From"
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                          />
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={priceMax}
                            onChange={(e) => setPriceMax(e.target.value)}
                            placeholder="To"
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                          />
                        </div>
                      </FilterSection>
                      {filterGroups.filter((group) => isFilterVisibleForCategory(group)).map((group) => (
                        <FilterSection
                          key={group.key}
                          id={`group-${group.key}`}
                          title={group.label}
                          openSection={openFilterSection}
                          setOpenSection={setOpenFilterSection}
                        >
                          {(group.key === "weaponPlatforms" || group.key === "brands") && (
                            <div className="relative mb-2">
                              <AiOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <input
                                type="text"
                                value={group.key === "weaponPlatforms" ? platformSearch : brandSearch}
                                onChange={(e) => {
                                  if (group.key === "weaponPlatforms") setPlatformSearch(e.target.value);
                                  if (group.key === "brands") setBrandSearch(e.target.value);
                                }}
                                placeholder={`Find ${group.label.toLowerCase()}...`}
                                className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                              />
                            </div>
                          )}
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {(group.options || [])
                              .filter((option) => {
                                const q = group.key === "weaponPlatforms" ? platformSearch : group.key === "brands" ? brandSearch : "";
                                return !q.trim() || (option.name || "").toLowerCase().includes(q.toLowerCase().trim());
                              })
                              .map((option) => {
                                const optionName = option.name;
                                const selectedSet =
                                  selectedFilterValues[group.key] instanceof Set
                                    ? selectedFilterValues[group.key]
                                    : new Set();
                                const checked = selectedSet.has(optionName);
                                return (
                                  <label key={`${group.key}-${optionName}`} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => {
                                        setSelectedFilterValues((prev) => {
                                          const current = prev[group.key] instanceof Set ? new Set(prev[group.key]) : new Set();
                                          if (current.has(optionName)) current.delete(optionName);
                                          else current.add(optionName);
                                          return { ...prev, [group.key]: current };
                                        });
                                        if (group.key === "brands") {
                                          setSelectedBrands((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(optionName)) next.delete(optionName);
                                            else next.add(optionName);
                                            return next;
                                          });
                                        }
                                        if (group.key === "colors") {
                                          setSelectedColors((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(optionName)) next.delete(optionName);
                                            else next.add(optionName);
                                            return next;
                                          });
                                        }
                                        if (group.key === "weaponPlatforms") {
                                          setSelectedWeaponPlatforms((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(optionName)) next.delete(optionName);
                                            else next.add(optionName);
                                            return next;
                                          });
                                        }
                                        if (group.key === "externalPartTypes") {
                                          setSelectedExternalPartTypes((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(optionName)) next.delete(optionName);
                                            else next.add(optionName);
                                            return next;
                                          });
                                        }
                                      }}
                                      className="rounded border-slate-300 text-[#38513b] focus:ring-[#38513b]"
                                    />
                                    <span className="text-sm text-slate-700">
                                      {optionName}
                                      {countsInData?.[group.key]?.[optionName] != null
                                        ? ` (${countsInData[group.key][optionName]})`
                                        : ""}
                                    </span>
                                  </label>
                                );
                              })}
                          </div>
                        </FilterSection>
                      ))}
                    </div>
                  </div>
                </aside>
                {/* Main content: counter + sort + grid */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <p className="text-sm text-slate-600">
                      Viewing <span className="font-medium text-slate-800">{filteredAndSortedProducts.length}</span> of <span className="font-medium text-slate-800">{data.length}</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <label htmlFor="sort-by" className="text-sm font-medium text-slate-700 whitespace-nowrap">Sort by</label>
                      <select
                        id="sort-by"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                      >
                        <option value="default">Default</option>
                        <option value="price-asc">Price: low to high</option>
                        <option value="price-desc">Price: high to low</option>
                        <option value="name-asc">Name: A to Z</option>
                        <option value="name-desc">Name: Z to A</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 md:gap-[20px] lg:grid-cols-4 lg:gap-[25px] xl:grid-cols-4 xl:gap-[30px] mb-12">
                    {filteredAndSortedProducts.slice(0, visibleCount).map((product) => (
                <ProductCard data={product} key={product._id} />
              ))}
            </div>
                  {filteredAndSortedProducts.length > visibleCount && (
                    <div className="flex justify-center mb-10">
                      <button
                        type="button"
                        onClick={() => setVisibleCount((prev) => prev + LOAD_MORE_STEP)}
                        className="rounded-lg bg-[#38513b] px-5 py-2 text-sm font-medium text-white hover:bg-[#2f4232]"
                      >
                        Load more products
                      </button>
                    </div>
                  )}
                  {filteredAndSortedProducts.length === 0 && (
                    <h2 className="text-center w-full pb-[100px] text-[20px] text-slate-600">No products found.</h2>
                  )}
                </div>
              </div>
            )}
          </div>
          <Footer />
        </div>
      )}
    </>
  );
};

export default ProductsPage;
